import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Maximize2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import NumberInput from '../NumberInput';
import { RECORD_TYPES } from '../../utils/recordUtils';
import { getPageScale } from '../../utils/pageScale';
import {
  composerDraftKey, thoughtDraftKey, pageDraftKey, loadDraft, saveDraft
} from '../../utils/recordDrafts';

const PLACEHOLDER = {
  thought: '이 책을 읽으며 떠오른 생각',
  quote: '책에서 옮겨 적고 싶은 문장'
};

// 자동 높이 조절 범위 (px)
const MIN_HEIGHT = 88;
const MAX_HEIGHT = 320;
const THOUGHT_MIN_HEIGHT = 56;

/** 내용에 맞춰 높이를 늘리되 최소·최대 안에 둔다 */
const autoGrow = (el, min) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(Math.max(el.scrollHeight, min), MAX_HEIGHT)}px`;
};

/**
 * 하단 고정 기록 입력창.
 * Tab 으로 생각↔인용 전환, Enter 저장, Shift+Enter 줄바꿈.
 *
 * 인용을 쓸 때는 아래에 '내 생각' 칸이 함께 열린다. 예전에는 인용을 먼저
 * 저장한 뒤에야 답글로 생각을 달 수 있었는데, 문장을 옮겨 적는 순간이
 * 곧 생각이 떠오르는 순간이라 한 번에 남길 수 있어야 한다.
 */
const RecordComposer = ({
  book,
  bookId,
  onSubmit,
  onExpand,
  parentId = null,
  onCancel = null,
  onSaved = null,
  lastPage = null,
  autoFocus = false
}) => {
  // 기본은 인용. 단, 인용에 답글로 다는 경우(parentId)는 '내 생각'이므로 생각 고정 —
  // 이때는 타입 토글이 렌더되지 않아서, 초기값이 곧 저장되는 타입이 된다.
  const [type, setType] = useState(
    parentId ? RECORD_TYPES.THOUGHT : RECORD_TYPES.QUOTE
  );
  // 초안은 휴대폰이 앱을 꺼도 남는다 (utils/recordDrafts)
  const [text, setText] = useState(() => loadDraft(composerDraftKey(bookId, parentId)));
  const [thought, setThought] = useState(() => (parentId ? '' : loadDraft(thoughtDraftKey(bookId))));
  const [page, setPage] = useState(() => loadDraft(pageDraftKey(bookId, parentId)));
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const thoughtRef = useRef(null);

  // 인용일 때만 '내 생각'을 함께 받는다 (답글 컴포저에는 없다)
  const withThought = !parentId && type === RECORD_TYPES.QUOTE;

  // 초안 임시 저장 — 전자책 앱에 다녀오는 사이 앱이 꺼져도 입력 내용 보존
  useEffect(() => {
    saveDraft(composerDraftKey(bookId, parentId), text);
  }, [text, bookId, parentId]);

  useEffect(() => {
    if (!parentId) saveDraft(thoughtDraftKey(bookId), thought);
  }, [thought, bookId, parentId]);

  useEffect(() => {
    saveDraft(pageDraftKey(bookId, parentId), page);
  }, [page, bookId, parentId]);

  // 내용에 맞춰 높이 자동 조절
  useEffect(() => { autoGrow(textareaRef.current, MIN_HEIGHT); }, [text]);
  useEffect(() => { autoGrow(thoughtRef.current, THOUGHT_MIN_HEIGHT); }, [thought, withThought]);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  const handleSubmit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;

    const extraThought = withThought ? thought.trim() : '';
    const canonicalPage = page
      ? getPageScale(book).toCanonical(parseInt(page, 10))
      : null;

    setSaving(true);
    try {
      const recordId = await onSubmit({
        type,
        text: trimmed,
        page: canonicalPage,
        parentId
      });

      // 인용에 딸린 생각은 방금 만든 인용의 자식으로 붙인다.
      // 부모 id 를 못 받았으면 조용히 사라지지 않게 알린다.
      if (extraThought) {
        if (recordId) {
          await onSubmit({
            type: RECORD_TYPES.THOUGHT,
            text: extraThought,
            page: canonicalPage,
            parentId: recordId
          });
        } else {
          toast.error('인용은 저장했지만 생각을 붙이지 못했어요');
        }
      }

      setText('');
      setThought('');
      setPage('');
      // 위 effect 가 지우기 전에 창이 닫혀 버릴 수 있어 여기서도 바로 지운다
      saveDraft(composerDraftKey(bookId, parentId), '');
      saveDraft(pageDraftKey(bookId, parentId), '');
      if (!parentId) saveDraft(thoughtDraftKey(bookId), '');
      if (onCancel) onCancel();
      // 인용과 생각이 모두 저장된 뒤에 알린다 (모달은 이 시점에 닫혀야 한다)
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.message || '기록에 실패했어요');
    } finally {
      setSaving(false);
    }
  }, [
    text, thought, withThought, saving, onSubmit,
    type, page, parentId, bookId, book, onCancel, onSaved
  ]);

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && !parentId) {
      e.preventDefault();
      setType(prev => prev === RECORD_TYPES.QUOTE ? RECORD_TYPES.THOUGHT : RECORD_TYPES.QUOTE);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      // 생각 칸에 쓰던 게 있으면 그쪽으로 넘어가지 않고 둘 다 저장한다
      handleSubmit();
      return;
    }
    if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  // 생각 칸에서는 Tab 으로 타입을 바꾸지 않는다 (칸 사이 이동이 자연스럽다)
  const handleThoughtKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit();
      return;
    }
    if (e.key === 'Escape' && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="surface p-3">
      <div className="flex items-center gap-2">
        {!parentId ? (
          <div className="flex gap-1">
            {[RECORD_TYPES.QUOTE, RECORD_TYPES.THOUGHT].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`chip !px-2.5 !py-1 !text-xs ${type === t ? 'chip-active' : ''}`}
              >
                {t === RECORD_TYPES.QUOTE ? '인용' : '생각'}
              </button>
            ))}
          </div>
        ) : (
          <span className="label">REPLY · 내 생각</span>
        )}

        <NumberInput
          min="1"
          value={page}
          onChange={e => setPage(e.target.value)}
          // 문장을 붙여넣고 쪽수를 마지막에 적는 경우가 많다. 여기서도 Enter 로 저장한다
          onKeyDown={handleThoughtKeyDown}
          placeholder={lastPage ? `p.${lastPage}` : 'p.'}
          className="!w-20 !px-2 !py-2 text-center"
          aria-label="페이지"
        />

        <div className="flex-1" />

        <span className="hidden sm:inline text-[11px] text-ink-500 dark:text-ink-400 font-sans">
          {parentId ? 'Enter 저장 · Esc 취소' : 'Tab 전환 · Enter 저장'}
        </span>

        {onExpand && (
          <button
            type="button"
            onClick={() => onExpand(type)}
            className="p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="전체화면으로 작성"
          >
            <Maximize2 size={14} strokeWidth={1.5} />
          </button>
        )}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 text-ink-500 dark:text-ink-400 hover:text-ink-900 dark:hover:text-ink-100 transition-colors"
            aria-label="취소"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        )}
      </div>

      <div className="mt-2 flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          lang="ko"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={PLACEHOLDER[type]}
          className={[
            'field resize-none !border-0 !px-2 !py-2 focus:!border-0 leading-relaxed',
            type === RECORD_TYPES.QUOTE ? 'font-display italic text-base' : ''
          ].join(' ')}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!text.trim() || saving}
          className="p-2 text-ink-500 hover:text-ink-900 dark:hover:text-ink-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="저장"
        >
          <Send size={16} strokeWidth={1.5} />
        </button>
      </div>

      {withThought && (
        <div className="mt-1 border-t border-ink-100 dark:border-ink-900 pt-2 pl-2 pr-11">
          <span className="label">내 생각 · 선택</span>
          <textarea
            ref={thoughtRef}
            value={thought}
            onChange={e => setThought(e.target.value)}
            onKeyDown={handleThoughtKeyDown}
            rows={2}
            lang="ko"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            placeholder="이 문장에 대해 떠오른 생각 (함께 저장돼요)"
            className="field mt-1 resize-none !border-0 !px-0 !py-1 focus:!border-0 text-sm leading-relaxed"
          />
        </div>
      )}
    </div>
  );
};

export default RecordComposer;
